import type { LocationConfigRow } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import React from 'react'
import type { StyleObject } from 'styletron-react'
import { useStyletron } from 'styletron-react'
import LocationAddressRow from './LocationAddressRow'

// Vertical gap between the selected-location cards (AI-901 spacing pass).
const ROW_GAP = '8px'

// Exported for the unit test — css() is a no-op under a plain render() (no
// StyletronProvider), so the card styling has to be asserted on the object,
// not the rendered DOM (mirrors StatusPill's exported statusPillStyle).
export const rowCardStyle: StyleObject = {
  backgroundColor: palette.white,
  border: `1px solid ${palette.grey20}`,
  borderRadius: '8px',
}

export interface SelectedLocationsListLabels {
  /**
   * aria-label builder for a row's remove button, e.g. name => `Remove ${name}`.
   * A function so the per-name %{name} interpolation happens in the consumer's
   * i18n layer, not in this presentational component.
   */
  removeAriaLabel: (name: string) => string
}

export interface SelectedLocationsListProps {
  /** The currently-selected locations, in display order (consumer owns order). */
  locations: LocationConfigRow[]
  /** Remove a location from the selection by its raw-int id. */
  onRemove: (id: number) => void
  /** Pre-translated, namespace-owned strings (the component is i18n-agnostic). */
  labels: SelectedLocationsListLabels
  /**
   * Render rows without remove controls. View-only operators see the active
   * locations as read-only state, with no write affordance.
   */
  readOnly?: boolean
}

/**
 * The removable address-row list for the Active Locations subset picker (AI-31).
 * Presentational only — no data fetching. Each row is keyed by its server-stable
 * `location.id` (NEVER the array index — a CLAUDE.md hard rule), so removing one
 * row never remounts the survivors. Each row is its own white bordered card with
 * a gap between them (not a flat divided list) — no dividing line between rows.
 */
const SelectedLocationsList = ({
  locations,
  onRemove,
  labels,
  readOnly = false,
}: SelectedLocationsListProps) => {
  const [css] = useStyletron()

  if (locations.length === 0) return null

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: ROW_GAP,
      })}
    >
      {locations.map(location => (
        <div
          // Server-stable id, not the index: a remove must not re-key survivors.
          key={location.id}
          className={css(rowCardStyle)}
        >
          <LocationAddressRow
            name={location.name}
            removeLabel={labels.removeAriaLabel(location.name)}
            onRemove={() => onRemove(location.id)}
            readOnly={readOnly}
          />
        </div>
      ))}
    </div>
  )
}

export default SelectedLocationsList
